import React, {useEffect, useState} from 'react';
import {Collapse, notification} from 'antd';
import {ModelIndex} from './models/model-index';
import {ParameterIndex} from './models/parameter-index';
import {SecuritySchemeIndex} from './models/security-scheme-index';

// Open the panel that matches the current view; close everything
// otherwise. Previously the fallback was ['models'] which caused the
// Schemas group to flash open for one frame then close on every
// non-models route — looked like a broken animation. Now Schemas only
// opens when the user is actually on a /models/* URL (or the matching
// component-type for parameters/securitySchemes).
const COMPONENT_VIEWS = new Set(['models', 'parameters', 'securitySchemes']);
function panelForParams(params) {
    if (!params || !params.view) return [];
    return COMPONENT_VIEWS.has(params.view) ? [params.view] : [];
}

export function ComponentIndex({currentHash, filter, params, onDelete, checkMobileView, schemaCount = 0, parameterCount = 0, responseCount = 0, securitySchemeCount = 0}) {
    // Initialize from params at first render — no transient default
    // → real-value swap, so the panel renders in its final state from
    // the very first paint.
    const [activePanel, setActivePanel] = useState(() => panelForParams(params));

    useEffect(() => {
        const next = panelForParams(params);
        // Only update if value actually changed; a same-value setState
        // would still trigger Collapse's open/close transition.
        setActivePanel((prev) => {
            if (prev.length === next.length && prev.every((k, i) => k === next[i])) return prev;
            return next;
        });
    }, [params.view]);

    function togglePanel(key) {
        setActivePanel(key);
    }

    function onDeleteParameter(parameter) {
        onDelete && onDelete({componentType: 'parameter', name: parameter.name});
        notification.success({title: `Parameter ${parameter.name} has been successfully deleted`, placement: 'bottomRight'});
    }

    function onDeleteSecurityScheme(securityScheme) {
        onDelete && onDelete({componentType: 'securityScheme', name: securityScheme.name});
        notification.success({title: `Security Scheme ${securityScheme.name} has been successfully deleted`, placement: 'bottomRight'});
    }

    const items = [
        schemaCount > 0 && {
            key: 'models',
            label: `Schemas (${schemaCount})`,
            children: <ModelIndex currentHash={currentHash} filter={filter} params={params} onDeleteModel={onDelete} checkMobileView={checkMobileView} />,
        },
        parameterCount > 0 && {
            key: 'parameters',
            label: `Parameters (${parameterCount})`,
            children: <ParameterIndex currentHash={currentHash} filter={filter} params={params} onDeleteModel={onDeleteParameter} checkMobileView={checkMobileView} />,
        },
        responseCount > 0 && {
            key: 'responses',
            label: 'Responses (0)',
            children: ' ',
        },
        securitySchemeCount > 0 && {
            key: 'securitySchemes',
            label: `Security Schemes (${securitySchemeCount})`,
            children: <SecuritySchemeIndex currentHash={currentHash} filter={filter} params={params} onDeleteModel={onDeleteSecurityScheme} checkMobileView={checkMobileView} />,
        },
    ].filter(Boolean);

    return <Collapse ghost={true} activeKey={activePanel} onChange={togglePanel} items={items} />;
}
